import React, { useState, useEffect, useCallback } from "react"
import "../styles/popUp.css"
import { XMarkIcon } from "@heroicons/react/24/outline"
import STRINGS from "../globals/strings"
import endpoints from "../api/endpoints"

function BatsmanPopUp({ batsman, onClose, matchId, matchType }) {
	const [battingStats, setBattingStats] = useState(null)
	const [ballsList, setBallsList] = useState([])
	const [matchSettings, setMatchSettings] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const fetchBatsmanHistory = useCallback(async () => {
		if (!matchId || !matchType || !batsman?.batsman_name) {
			setError(STRINGS.MISSING_PARAMETERS_BATSMAN)
			setBattingStats(null)
			setBallsList([])
			setMatchSettings(null)
			return
		}

		try {
			setLoading(true)
			setError(null)

			console.log(`Sending BatsmanHistory API request:`, { matchId, matchType, batsman })

			const result = await endpoints.fetchBatsmanHistory(matchId, matchType, batsman)

			if (result.success && result.data) {
				const data = result.data

				setMatchSettings(data.match_settings || null)
				setBattingStats({
					batsman_name: data.batsman_name || batsman.batsman_name,
					total_runs: data.total_runs || 0,
					total_balls: data.total_balls || 0,
					total_fours: data.total_fours || 0,
					total_sixes: data.total_sixes || 0,
					total_dot_balls: data.total_dot_balls || 0
				})

				const mappedBalls = (data.balls_data || []).map(ball => ({
					runs: ball.runs !== undefined ? ball.runs : 0,
					is_four: !!ball.is_four,
					is_six: !!ball.is_six,
					is_out: !!ball.is_out,
					is_wide_ball: !!ball.is_wide_ball,
					is_no_ball: !!ball.is_no_ball,
					is_bye: !!ball.is_bye,
					is_leg_bye: !!ball.is_leg_bye
				}))
				setBallsList(mappedBalls)
			} else {
				setMatchSettings(null)
				setBattingStats(null)
				setError(result.message || STRINGS.NO_BALL_DATA)
				setBallsList([])
			}
		} catch (err) {
			setError(`${STRINGS.FAILED_TO_FETCH_BATSMAN_HISTORY}${err.message}`)
			setBattingStats(null)
			setBallsList([])
			setMatchSettings(null)
		} finally {
			setLoading(false)
		}
	}, [batsman, matchId, matchType])

	useEffect(() => {
		fetchBatsmanHistory()
		document.body.classList.add("modal-open")
		return () => {
			document.body.classList.remove("modal-open")
		}
	}, [fetchBatsmanHistory])

	const getDotColor = (ball) => {
		if (ball.is_out) return "dot red"
		if (ball.is_six) return "dot green"
		if (ball.is_four) return "dot green"
		if (ball.is_wide_ball) return "dot gray"
		if (ball.is_no_ball) return "dot gray"
		if (ball.is_bye || ball.is_leg_bye) return "dot gray"
		return "dot gray"
	}

	// Returns the run value to display based on match_settings.
	// If a ball type has allowed = 1, subtract 1 from runs (the extra run is a penalty, not a scored run).
	// If allowed = 0, show runs as-is.
	const getDisplayRuns = (ball) => {
		if (!matchSettings) return ball.runs

		if (ball.is_wide_ball && matchSettings.is_wide_ball_allowed === 1) return ball.runs - 1
		if (ball.is_no_ball && matchSettings.is_no_ball_allowed === 1) return ball.runs - 1

		return ball.runs
	}

	return (
		<div className="pop-up">
			<div className="pop-up-header" style={{ borderBottom: "none", height: "auto" }}>
				<label className="bowling-label" style={{ justifyContent: "space-between", flexWrap: "nowrap" }}>
					<span style={{ whiteSpace: "nowrap" }}>{STRINGS.BATSMAN_HIGHLIGHTS}</span>
					<XMarkIcon
						className="cancel-icon"
						onClick={onClose}
						style={{ height: "30px", width: "30px", marginLeft: 0, flexShrink: 0 }}
					/>
				</label>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
					<label className="name-label" style={{ paddingTop: 0, whiteSpace: "nowrap" }}>
						{STRINGS.BATSMAN_NAME_STATS(
							battingStats?.batsman_name || batsman?.batsman_name || STRINGS.UNKNOWN_BATSMAN,
							battingStats?.total_runs || 0,
							battingStats?.total_balls || 0
						)}
					</label>
					<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
						<span style={{ fontSize: "14px", fontWeight: "600", color: "#717171" }}>
							<span style={{ color: "#10B981", fontWeight: "700" }}>{battingStats?.total_sixes || 0}</span> {STRINGS.SIXES_HEADER}
						</span>
						<span style={{ fontSize: "14px", fontWeight: "600", color: "#717171" }}>
							<span style={{ color: "#10B981", fontWeight: "700" }}>{battingStats?.total_fours || 0}</span> {STRINGS.FOURS_HEADER}
						</span>
						<span style={{ fontSize: "14px", fontWeight: "600", color: "#717171" }}>
							<span style={{ color: "#030311", fontWeight: "700" }}>{battingStats?.total_dot_balls || 0}</span> {STRINGS.DOT_BALLS_HEADER}
						</span>
					</div>
				</div>
				<hr style={{ border: "none", borderTop: "2px solid #ECECEE", margin: "20px -20px 0" }} />
			</div>
			<div className="scrollable-popup-contant">
				{loading ? (
					<div className="loader">{STRINGS.LOADING_BATSMAN_HIGHLIGHTS}</div>
				) : error ? (
					<div className="error-message">
						<p>{error}</p>
					</div>
				) : ballsList.length > 0 ? (
					<div className="over-row">
						<div className="over-dots">
							<div className="dots-container">
								{ballsList.map((ball, idx) => {
									const isSpecial = ball.is_wide_ball || ball.is_no_ball || ball.is_bye || ball.is_leg_bye

									const getLabel = () => {
										const displayRuns = getDisplayRuns(ball)
										const parts = []

										const fmt = (runs, type) => runs > 0 ? `${runs}${type}` : type

										if (ball.is_wide_ball) parts.push(fmt(ball.is_out ? displayRuns : 0, "WD"))
										else if (ball.is_no_ball) parts.push(fmt(ball.is_out ? displayRuns : 0, "NB"))
										else if (ball.is_bye) parts.push(fmt(ball.is_out ? displayRuns : 0, "BYE"))
										else if (ball.is_leg_bye) parts.push(fmt(ball.is_out ? displayRuns : 0, "LB"))

										if (ball.is_out && parts.length === 0) {
											parts.push(fmt(displayRuns, "WK"))
										}

										return parts
									}

									const labels = getLabel()

									return (
										<div key={idx} style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
										}}>
											<span className={isSpecial && ball.is_out ? "dot red" : getDotColor(ball)}>
												{isSpecial && ball.is_out ? "W" : getDisplayRuns(ball)}
											</span>
											{labels.length > 0 && (
												<div style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													marginTop: "5px",
													gap: "1px"
												}}>
													{labels.map((label, i) => (
														<span key={i} style={{
															fontWeight: "500",
															fontSize: "12px",
															lineHeight: "14px",
															textAlign: "center",
															color: "#717171",
															whiteSpace: "nowrap"
														}}>
															{label}
														</span>
													))}
												</div>
											)}
										</div>
									)
								})}
							</div>
						</div>
					</div>
				) : (
					<div className="no-data">
						<p>{STRINGS.NO_BALL_HISTORY}</p>
					</div>
				)}
			</div>
		</div>
	)
}

export default BatsmanPopUp
